import React from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  LinearProgress,
  Chip,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
} from '@mui/icons-material';

interface FranchiseeCompliance {
  code: string;
  name: string;
  complianceScore: number;
  franchiseeOwner?: string;
}

interface SOPAdoptionHeatMapProps {
  franchisees: FranchiseeCompliance[];
  maxItems?: number;
}

const getComplianceConfig = (score: number) => {
  if (score >= 90) return { color: '#2E7D32', bg: '#E8F5E9', label: 'Compliant', Icon: CheckCircleIcon };
  if (score >= 85) return { color: '#F57C00', bg: '#FFF3E0', label: 'At Risk', Icon: WarningIcon };
  return { color: '#D32F2F', bg: '#FFEBEE', label: 'Non-Compliant', Icon: ErrorIcon };
};

export const SOPAdoptionHeatMap: React.FC<SOPAdoptionHeatMapProps> = ({ franchisees, maxItems = 12 }) => {
  // Sort by compliance score ascending (worst first) for visibility
  const sorted = [...franchisees]
    .filter((f) => f.complianceScore != null)
    .sort((a, b) => a.complianceScore - b.complianceScore)
    .slice(0, maxItems);

  // Stats
  const total = franchisees.filter((f) => f.complianceScore != null).length;
  const compliant = franchisees.filter((f) => (f.complianceScore ?? 0) >= 90).length;
  const atRisk = franchisees.filter((f) => {
    const s = f.complianceScore ?? 0;
    return s >= 85 && s < 90;
  }).length;
  const nonCompliant = franchisees.filter((f) => (f.complianceScore ?? 0) < 85).length;
  const adoptionRate = total > 0 ? Math.round((compliant / total) * 100) : 0;

  return (
    <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1rem' }}>
            SOP Adoption
          </Typography>
          <Typography
            sx={{
              fontSize: '1.4rem',
              fontWeight: 800,
              color: adoptionRate >= 80 ? '#2E7D32' : adoptionRate >= 60 ? '#F57C00' : '#D32F2F',
            }}
          >
            {adoptionRate}%
          </Typography>
        </Box>

        {/* Summary chips */}
        <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
          <Chip
            size="small"
            icon={<CheckCircleIcon sx={{ fontSize: 14 }} />}
            label={`${compliant} Compliant`}
            sx={{ bgcolor: '#E8F5E9', color: '#2E7D32', fontWeight: 600, fontSize: '0.72rem' }}
          />
          <Chip
            size="small"
            icon={<WarningIcon sx={{ fontSize: 14 }} />}
            label={`${atRisk} At Risk`}
            sx={{ bgcolor: '#FFF3E0', color: '#F57C00', fontWeight: 600, fontSize: '0.72rem' }}
          />
          <Chip
            size="small"
            icon={<ErrorIcon sx={{ fontSize: 14 }} />}
            label={`${nonCompliant} Non-Compliant`}
            sx={{ bgcolor: '#FFEBEE', color: '#D32F2F', fontWeight: 600, fontSize: '0.72rem' }}
          />
        </Box>

        {/* Franchisee list */}
        <Box sx={{ flex: 1, overflow: 'auto', maxHeight: 320 }}>
          {sorted.map((franchisee) => {
            const config = getComplianceConfig(franchisee.complianceScore);
            return (
              <Box
                key={franchisee.code}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  py: 1,
                  px: 1,
                  borderRadius: 1,
                  mb: 0.5,
                  bgcolor: `${config.bg}60`,
                  transition: 'background-color 0.2s',
                  '&:hover': { bgcolor: config.bg },
                }}
              >
                <config.Icon sx={{ fontSize: 18, color: config.color, flexShrink: 0 }} />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    sx={{
                      fontSize: '0.82rem',
                      fontWeight: 600,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {franchisee.name}
                  </Typography>
                </Box>
                <Box sx={{ width: 80, flexShrink: 0 }}>
                  <LinearProgress
                    variant="determinate"
                    value={franchisee.complianceScore}
                    sx={{
                      height: 6,
                      borderRadius: 3,
                      backgroundColor: `${config.color}20`,
                      '& .MuiLinearProgress-bar': {
                        backgroundColor: config.color,
                        borderRadius: 3,
                      },
                    }}
                  />
                </Box>
                <Typography
                  sx={{
                    fontSize: '0.8rem',
                    fontWeight: 700,
                    color: config.color,
                    minWidth: 36,
                    textAlign: 'right',
                  }}
                >
                  {franchisee.complianceScore}%
                </Typography>
              </Box>
            );
          })}
        </Box>
      </CardContent>
    </Card>
  );
};
